import { ReactNode, createContext, useCallback, useContext, useEffect } from 'react'
import ReactGA from 'react-ga4'

import { ANALYTICS_ENABLED, GOOGLE_TRACKING_ANALYTICS_ID } from '@/config'
import { useAnalyticsAcceptance } from '@/hooks'

type AnalyticsContext = {
  sendButtonClickEvent: (label: string) => void
}

const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { analytics } = useAnalyticsAcceptance()

  const removeCookie = (name: string) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
  }

  const initializeGA = () => {
    ReactGA.gtag('consent', 'default', {
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      analytics_storage: 'denied',
    })

    if (!ReactGA.isInitialized) {
      ReactGA.initialize([
        {
          trackingId: `G-${GOOGLE_TRACKING_ANALYTICS_ID}`,
          gtagOptions: {
            anonymizeIp: true,
            allow_google_signals: false,
          },
        },
      ])
    }
  }

  const updateConsent = useCallback(() => {
    ReactGA.gtag('consent', 'update', {
      analytics_storage: analytics.isGoogleAnalyticsEnabled ? 'granted' : 'denied',
    })

    if (!analytics.isGoogleAnalyticsEnabled) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ;(window as any)[`ga-disable-G-${GOOGLE_TRACKING_ANALYTICS_ID}`] = true
      removeCookie('_ga')
      removeCookie(`_ga_${GOOGLE_TRACKING_ANALYTICS_ID}`)
      removeCookie(`ai_user`)
      removeCookie(`ai_session`)
    }
  }, [analytics.isGoogleAnalyticsEnabled])

  const sendButtonClickEvent = useCallback(
    (label: string) => {
      if (ReactGA.isInitialized && analytics.isGoogleAnalyticsEnabled) {
        ReactGA.event({
          action: 'click',
          category: 'button',
          label,
        })
      }
    },
    [analytics.isGoogleAnalyticsEnabled]
  )

  useEffect(() => {
    if (ANALYTICS_ENABLED) {
      initializeGA()
      updateConsent()
    }
  }, [updateConsent])

  return <analyticsContext.Provider value={{ sendButtonClickEvent }}>{children}</analyticsContext.Provider>
}

const analyticsContext = createContext<AnalyticsContext>({
  sendButtonClickEvent: () => {
    throw new Error('context is missing')
  },
})

const useAnalyticsContext = () => {
  return useContext(analyticsContext)
}

// Rule not needed for this file
// eslint-disable-next-line react-refresh/only-export-components
export { AnalyticsProvider, useAnalyticsContext }
